import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, FileText, FileImage, FileVideo, File as FileIcon } from 'lucide-react';

const MAX_BYTES = 50 * 1024 * 1024;

const typeIconFor = (file) => {
  const ct = file?.type || '';
  if (ct.startsWith('image/')) return FileImage;
  if (ct.startsWith('video/')) return FileVideo;
  if (ct.includes('pdf')) return FileText;
  return FileIcon;
};

const fmtBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Drag-and-drop + click-to-select file picker.
 * Controlled component: pass `files` array + `onFilesChange(newFiles)`.
 * Shows a thumbnail list of pending files with remove buttons.
 *
 * Props:
 *  - files:    File[]
 *  - onFilesChange: (File[]) => void
 *  - accept:   string (HTML accept attribute)
 *  - multiple: boolean (default true)
 *  - disabled: boolean
 *  - label:    string (hint under icon)
 *  - compact:  boolean — smaller variant for inline use in chat/comment area
 *  - testIdPrefix: string
 */
const FileDropzone = ({
  files = [],
  onFilesChange,
  accept = 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',
  multiple = true,
  disabled = false,
  label = 'Poze, Video, PDF, Word, Excel (max 50MB/fisier)',
  compact = false,
  testIdPrefix = 'file-dropzone',
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleAddFiles = useCallback((newList) => {
    const arr = Array.from(newList || []);
    const valid = [];
    for (const f of arr) {
      if (f.size > MAX_BYTES) {
        // Surface via window.alert fallback; caller can wire toast via effect if needed
        try { window?.toast?.error?.(`${f.name} depaseste 50MB`); } catch { /* noop */ }
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) return;
    onFilesChange(multiple ? [...files, ...valid] : [valid[0]]);
  }, [files, multiple, onFilesChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer?.files?.length) handleAddFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => { e.preventDefault(); if (!disabled) setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const removeAt = (idx) => onFilesChange(files.filter((_, i) => i !== idx));

  const zoneClasses = [
    'border-2 border-dashed rounded-lg transition-colors text-center',
    compact ? 'p-2' : 'p-4',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50',
  ].join(' ');

  return (
    <div className="space-y-2">
      <div
        className={zoneClasses}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        data-testid={`${testIdPrefix}-zone`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          hidden
          onChange={(e) => { handleAddFiles(e.target.files); if (e.target) e.target.value = ''; }}
          data-testid={`${testIdPrefix}-input`}
          disabled={disabled}
        />
        <Upload className={`mx-auto ${compact ? 'w-5 h-5' : 'w-8 h-8'} text-slate-400 ${compact ? 'mb-1' : 'mb-2'}`} />
        <p className={`${compact ? 'text-xs' : 'text-sm'} text-slate-600 font-medium`}>
          {compact ? 'Click sau trage fisiere aici' : 'Click sau trage fisiere aici pentru a atasa'}
        </p>
        {!compact && (
          <p className="text-[11px] text-slate-400 mt-1">{label}</p>
        )}
      </div>

      {files.length > 0 && (
        <ul className="space-y-1.5" data-testid={`${testIdPrefix}-list`}>
          {files.map((f, idx) => {
            const Icon = typeIconFor(f);
            return (
              <li
                key={`${f.name}-${idx}`}
                className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-100"
                data-testid={`${testIdPrefix}-pending-${idx}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0 text-indigo-500" />
                <span className="text-xs text-slate-700 flex-1 truncate" title={f.name}>{f.name}</span>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmtBytes(f.size)}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
                  className="p-1 rounded hover:bg-rose-100 text-rose-500"
                  title="Elimina"
                  data-testid={`${testIdPrefix}-pending-remove-${idx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FileDropzone;
